import _ from 'lodash';
import { useToastV3 } from '@onereach/ui-components-25';

import { useCommonProps } from './use-common-props';
import { useDialerSetters, useDialerStore } from './use-dialer-store';
import { getAgentName, getContactName } from '@/common';
import { ConferenceMember, CALL_MEMBER_TYPE, CALL_MEMBER_STATUS } from '../types';
import { CALL_MEMBER_STATUS_LABEL } from '../constants';

/** Actions for call members, like "Get Members" or "Disconnect Member" */
export function useConferenceMembersActions() {
  const props = useCommonProps();
  const { notify } = useToastV3();
  const store = useDialerStore();
  const setters = useDialerSetters();

  const getConferenceMembers = async () => {
    if (!store.sessionId) {
      return;
    }

    /* Conference members are only requested once per call */
    if (!_.isEmpty(store.membersById) || store.isLoadingMembers) {
      return;
    }

    try {
      store.isLoadingMembers = true;
      const response = await props.getConferenceMembers({
        sessionId: store.sessionId,
      });

      setters.setMembers(_.keyBy(response.data, 'memberId'));
    } catch (e) {
      console.error('Error while requesting conference members!', e);
    } finally {
      store.isLoadingMembers = false;
    }
  };

  /** Add, update or remove conference member based on their status */
  const onUpsertConferenceMember = (args: { member: ConferenceMember; }) => {
    const { member } = args;

    if (store.sessionId !== args.member.hitlSessionId) {
      return;
    }

    /* TODO: Might not work properly when multiple contacts are supported */
    const isContactEvent = member.memberId === store.contactId;
    if (isContactEvent) {
      const isOnHold = member.status === CALL_MEMBER_STATUS.ON_HOLD;
      store.isContactOnHold = isOnHold;
    }

    /* Remove member from conference and optionally notify active agent about the reason */
    const ERROR_JOIN_EVENTS = [CALL_MEMBER_STATUS.BUSY, CALL_MEMBER_STATUS.NOT_CONNECTED];
    const ERROR_LEAVE_EVENTS = [CALL_MEMBER_STATUS.DISCONNECTED, CALL_MEMBER_STATUS.ERROR];
    const EVENTS = [...ERROR_JOIN_EVENTS, ...ERROR_LEAVE_EVENTS, CALL_MEMBER_STATUS.HANG_UP];

    const { memberId, status, identifier } = member;
    if (EVENTS.includes(status)) {
      setters.removeMember(memberId);

      const isErrorJoinEvent = ERROR_JOIN_EVENTS.includes(status);
      const isErrorLeaveEvent = ERROR_LEAVE_EVENTS.includes(status);

      if (isErrorJoinEvent || isErrorLeaveEvent) {
        const reason = props.$t(CALL_MEMBER_STATUS_LABEL[status]);

        const name = member.type === CALL_MEMBER_TYPE.CONTACT
          ? getContactName(props.getContactById.value(member.memberId)!, props.$t)
          : member.type === CALL_MEMBER_TYPE.AGENT
            ? getAgentName(props.getAgentById.value(member.memberId), props.$t)
            : member.identifier;

        const content = {
          name: `<strong>${_.truncate(name, { length: 25 })}</strong>`,
          reason: `<strong>${reason}</strong>`,
        };

        const message = isErrorJoinEvent
          ? props.$t('hitl.modules.dialer.member-cannot-connect', content)
          : props.$t('hitl.modules.dialer.member-leave-conference', content);

        console.debug(message);
        notify({ messageText: message as string, dismissible: true });
      }

      return;
    }

    setters.setMember(member);

    /* During outgoing call we receive contact's phone number after they join */
    if (isContactEvent && identifier) {
      store.identifier = identifier;
    }
  };

  const onHangUp = async (memberId: string) => {
    const member = store.membersById[memberId];
    const isAgent = member.type === CALL_MEMBER_TYPE.AGENT;
    const isThirdParty = member.type === CALL_MEMBER_TYPE.THIRD_PARTY;

    const payload = {
      command: isAgent ? 'remove_agent' : 'remove_3rdparty',
      sessionId: store.sessionId,
      data: {
        ...(isAgent ? { agentId: memberId } : {}),
        ...(isThirdParty ? { number: member.identifier } : {}),
      },
    };

    try {
      store.isPerformingAction = true;
      await props.applyCommand(payload);
    } catch (e) {
      console.error('Error while trying to hang up call for the member!', e);
    } finally {
      store.isPerformingAction = false;
    }
  };

  return { getConferenceMembers, onUpsertConferenceMember, onHangUp };
}
